import { createReducer } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import set from 'lodash/set';
import { RequestState } from 'modules/types/common';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from '../actions';
import {
  ClinicNotesFilter,
  ClinicNotesFilterOption,
  ClinicNotesFilterOptions,
  ClinicNotesFilterType,
  ClinicNotesListState,
  ClinicNotesSortBy,
  DateTimeOption,
  filterOptionValueGetters
} from '../types';
import { getClinicNotesFilterOptions } from '../utils';

export const initialState: ClinicNotesListState = {
  isFetching: false,
  error: null,
  data: null,
  sortBy: ClinicNotesSortBy.date,
  filters: [],
  filterOptions: null,
  lastUpdated: undefined,
  isSending: false,
  requestState: RequestState.INITIALIZED
};

export interface ShareClinicNotesState {
  isSending: boolean;
}

const currentDate = new Date();
// FROM date in the past TO date in the preset/future
export const initialDateRange = { from: dayjs(currentDate), to: dayjs(currentDate) };

const clinicalNotesList = createReducer(initialState, {
  [actions.GET_LIST]: state => {
    state.isFetching = true;
    state.error = null;
    state.requestState = RequestState.REQUESTING;
  },
  [actions.GET_CLINIC_NOTES_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.lastUpdated = action.payload.cacheUpdatedAt;
    state.filterOptions = getClinicNotesFilterOptions(state, action, initialDateRange);
  },
  [actions.GET_LIST_SUCCESS]: (state, action) => {
    state.isFetching = false;
    state.data = action.payload.data;
    // @TODO: Add support for filter by providers when service adds providers.
    // Sort all unique provider names from list data to render as filter options
    // const providers = sortBy(
    //   map(uniqBy(action.payload.data, 'provider'), provider => {
    //     return {
    //       ...provider,
    //       filterType: ClinicNotesFilterType.provider,
    //       checked: false
    //     };
    //   }),
    //   (item: ClinicNotesFilterOption) => item.provider
    // );
    state.filterOptions = getClinicNotesFilterOptions(state, action, initialDateRange);
    state.requestState = RequestState.SUCCESS;
  },

  [actions.GET_LIST_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
    state.requestState = RequestState.ERROR;
  },

  [actions.UPDATE_DATE_RANGE]: (state, action) => {
    const newDateRange = {
      from: action.payload.from,
      to: action.payload.to
    };
    set(state, 'filterOptions.dateTime.[3].value', newDateRange);
  },

  [actions.UPDATE_FILTER_OPTIONS]: (state, action) => {
    // Assign new mutable options
    const newFilterOptions = state.filterOptions as ClinicNotesFilterOptions;
    // Get the filter option type from the payload
    const filterOptionType = get(newFilterOptions, action.payload.filterType);
    // Get the index from the option that matches the payload id
    const index = findIndex(filterOptionType, ['id', action.payload.id]);

    // If filter type is dateTime or dateRange, uncheck all dateTime and dateRange options first
    // as only one can be checked at a time.
    if (
      action.payload.filterType === ClinicNotesFilterType.dateTime ||
      action.payload.filterType === ClinicNotesFilterType.dateRange
    ) {
      const dateTimeOptions = get(newFilterOptions, ClinicNotesFilterType.dateTime).map(
        (option: DateTimeOption) => ({
          ...option,
          checked: false
        })
      );
      set(newFilterOptions, ClinicNotesFilterType.dateTime, dateTimeOptions);
    }

    // If filter type is dateRange, set the chacked value in the dateTime options
    // which is always going to be the index 3 optioin as set in the GET_LIST_SUCCESS
    // reducer above.
    if (action.payload.filterType === ClinicNotesFilterType.dateRange) {
      set(newFilterOptions, 'dateTime.3.checked', action.payload.checked);
      set(newFilterOptions, 'dateTime.3.value', initialDateRange);
    } else {
      // Else, set checked to true for the new filter type option
      set(
        newFilterOptions,
        `${action.payload.filterType}.${index}.checked`,
        action.payload.checked
      );
    }

    state.filterOptions = newFilterOptions;
  },

  [actions.APPLY_CLINIC_NOTES_LIST_FILTERS]: state => {
    // Assign new mutable filters array
    let newFilters = [] as ClinicNotesFilter[];
    // Iterate over ClinicNotesFilterTypes to derive filters from all filter options
    Object.values(ClinicNotesFilterType).forEach((type: string) => {
      // Get current iterated filter option type
      const filterOptionType = get(state.filterOptions, type);
      // If filter option type is available from inital filter options, construct
      // filter objects array.
      if (filterOptionType) {
        // Construct filter objects array only out of filter options that are currently checked
        const reducedFilters = filterOptionType?.reduce(
          (acc: ClinicNotesFilter[], option: ClinicNotesFilterOption | DateTimeOption) => {
            const isChecked = option?.checked;
            // If checked, construct filter object to be added to filters array
            if (isChecked) {
              // Derive object path to filterable values
              const filterGetters = filterOptionValueGetters[option?.filterType];
              // Accumulate the filter objects into a filters array
              acc.push({
                filterType: option.filterType,
                value: get(option, filterGetters)
              });
            }
            return acc;
          },
          []
        );
        // On each iteration of ClinicNotesFilterType we want to merge what has already
        // been passed into the newFilters with the reducedFilters in the current iteration.
        newFilters = [...newFilters, ...reducedFilters];
      }
    });
    state.filters = newFilters;
  },

  [actions.RESET_CLINIC_NOTES_LIST_FILTERS]: state => {
    // Assign new mutable filter options object
    const newFilterOptions = {} as ClinicNotesFilterOptions;
    // Iterate over ClinicNotesFilterTypes to reset all filter options
    Object.values(ClinicNotesFilterType).forEach((type: string) => {
      // Get current iterated filter option type
      const filterOptionType = get(state.filterOptions, type);
      // If filter option type is available from inital filter options, map over
      // filter options to uncheck all options and reset dateRange value
      if (filterOptionType) {
        const mappedFilterOptions = filterOptionType?.map(
          (option: ClinicNotesFilterOption | DateTimeOption) => {
            // If filter type is dateRange, reset 'to' and 'from' values
            if (option.filterType === ClinicNotesFilterType.dateRange) {
              return { ...option, checked: false, value: initialDateRange };
            }
            // Otherwise, uncheck the filter option
            return { ...option, checked: false };
          }
        );
        set(newFilterOptions, type, mappedFilterOptions);
      }
    });

    state.filterOptions = newFilterOptions;
    state.filters = initialState.filters;
  },

  [actions.UPDATE_SORT]: (state, action) => {
    state.sortBy = action.payload.sortBy;
  },

  [switchAccountSuccess.type]: () => initialState
});

export const shareClinicNotesInitialState = (): ShareClinicNotesState => ({
  isSending: false
});

export const shareClinicNotesReducer = createReducer(shareClinicNotesInitialState(), {
  [actions.SHARE_DOCUMENT]: state => {
    state.isSending = true;
  },
  [actions.SHARE_DOCUMENT_SUCCESS]: state => {
    state.isSending = false;
  },
  [actions.SHARE_DOCUMENT_FAIL]: state => {
    state.isSending = false;
  }
});

export default clinicalNotesList;
